import { __extends } from "tslib";
/**
* PropertiesPlugin.ts
* @author Abhilash Panwar (abpanwar) Hector Hernandez (hectorh)
* @copyright Microsoft 2018
*/
import dynamicProto from "@microsoft/dynamicproto-js";
import { BaseTelemetryPlugin, arrForEach, createProcessTelemetryContext, objForEachKey, objKeys, onConfigChange, setProcessTelemetryTimings } from "@microsoft/1ds-core-js";
import { cfgDfMerge, cfgDfString } from "@microsoft/applicationinsights-core-js";
import { objDeepFreeze } from "@nevware21/ts-utils";
import { Extensions } from "./ExtensionKeys";
import { TelemetryContext } from "./TelemetryContext";
var undefValue;
var extensions = [
    Extensions[4 /* eExtensions.AppExt */],
    Extensions[0 /* eExtensions.UserExt */],
    Extensions[3 /* eExtensions.WebExt */],
    Extensions[5 /* eExtensions.OSExt */],
    Extensions[6 /* eExtensions.SdkExt */],
    Extensions[7 /* eExtensions.IntWebExt */],
    Extensions[8 /* eExtensions.UtcExt */],
    Extensions[9 /* eExtensions.LocExt */],
    Extensions[1 /* eExtensions.DeviceExt */],
    Extensions[2 /* eExtensions.TraceExt */],
    Extensions[11 /* eExtensions.DtExt */],
    Extensions[10 /* eExtensions.CloudExt */]
];
var defaultPropertyConfig = objDeepFreeze({
    populateBrowserInfo: false,
    populateOperatingSystemInfo: false,
    userAgent: cfgDfString(),
    userAgentData: cfgDfMerge({
        brands: undefValue,
        mobile: undefValue,
        platform: undefValue
    }),
    userConsentCookieName: cfgDfString(),
    userConsented: false,
    serviceName: cfgDfString(),
    env: cfgDfString(),
    expId: cfgDfString(),
    sessionRenewalMs: 1800000,
    sessionExpirationMs: 86400000,
    sessionAsGuid: null,
    cookieDomain: cfgDfString(),
    namePrefix: cfgDfString(),
    enableApplicationInsightsTrace: false,
    enableApplicationInsightsUser: false,
    hashIdentifiers: false,
    dropIdentifiers: false,
    scrubIpOnly: false,
    callback: cfgDfMerge({
        userConsentDetails: null
    }),
    gpcDataSharingOptIn: undefValue,
    idLength: 22,
    enableDistributedTracing: false,
    eventContainExtFields: false // Added in v3.2.5
});
var PropertiesPlugin = /** @class */ (function (_super) {
    __extends(PropertiesPlugin, _super);
    function PropertiesPlugin() {
        var _this = _super.call(this) || this;
        _this.identifier = "SystemPropertiesCollector";
        _this.priority = 3;
        _this.version = '4.3.9';
        // Do not set default values here, set them in the _initDefaults() which is also called during teardown()
        var _context;
        var _properties;
        var _config;
        dynamicProto(PropertiesPlugin, _this, function (_self, _base) {
            _initDefaults();
            _self.initialize = function (coreConfig, core, extensions) {
                _base.initialize(coreConfig, core, extensions);
                _populateDefaults(coreConfig);
            };
            /**
             * Process the event and add part A fields to it.
             * @param event - The event that needs to be stored.
             * @param itemCtx - This is the context for the current request, ITelemetryPlugin instances
             * can optionally use this to access the current core instance or define / pass additional information
             * to later plugins (vs appending items to the telemetry item)
             */
            _self.processTelemetry = function (event, itemCtx) {
                setProcessTelemetryTimings(event, _self.identifier);
                itemCtx = _self._getTelCtx(itemCtx);
                var evtExt = event.ext = event.ext ? event.ext : {};
                event.data = event.data ? event.data : {};
                arrForEach(extensions, function (value) {
                    evtExt[value] = evtExt[value] || {};
                });
                if (_context) {
                    // Always apply the utc properties first as this sets the `ext.utc.eventFlags` which is required to tell
                    // the collector to mask some fields
                    _context.applyUtcContext(event);
                    _context.applyApplicationContext(event);
                    _context.applyUserContext(event);
                    _context.applyWebContext(event);
                    _context.applyOsContext(event);
                    _context.applySdkContext(event);
                    _context.applyIntWebContext(event);
                    _context.applyLocContext(event);
                    _context.applySessionContext(event);
                    _context.applyDeviceContext(event);
                    if (_config.enableApplicationInsightsTrace) {
                        _context.applyAITraceContext(event);
                    }
                    if (_config.enableDistributedTracing) {
                        _context.applyDistributedTraceContext(event);
                    }
                    _context.applyCloudContext(event);
                }
                // Delete empty ext fields
                arrForEach(objKeys(evtExt), function (key) {
                    if (objKeys(evtExt[key]).length === 0) {
                        delete evtExt[key];
                    }
                });
                // Add custom properties
                _addPropertiesIfAbsent(_properties, event.data);
                _self.processNext(event, itemCtx);
            };
            /**
             * Get properties context to override or specify specific part A properties
             * @returns the Context
             */
            _self.getPropertiesContext = function () {
                return _context;
            };
            /**
             * Sets a custom property to be sent with every event. IEventProperty can be used to tag the property as
             * pii or customer content.
             * @param name - The name of the property.
             * @param value - The context property's value.
             */
            _self.setProperty = function (name, value) {
                _properties[name] = value;
            };
            _self._doTeardown = function (unloadCtx, unloadState) {
                var core = (unloadCtx || {}).core();
                if (core && core.getTraceCtx && _context) {
                    var traceCtx = core.getTraceCtx(false);
                    if (traceCtx && traceCtx === _context.getTraceCtx()) {
                        core.setTraceCtx(null);
                    }
                }
                _initDefaults();
            };
            _self["_getDbgPlgTargets"] = function () {
                return [_config];
            };
            function _initDefaults() {
                _context = null;
                _properties = {};
            }
            function _populateDefaults(config) {
                var identifier = _self.identifier;
                var core = _self.core;
                // This function will be re-called whenever any referenced configuration is changed
                _self._addHook(onConfigChange(config, function () {
                    var ctx = createProcessTelemetryContext(null, config, core);
                    // Apply and re-apply defaults
                    _config = ctx.getExtCfg(identifier, defaultPropertyConfig);
                }));
                // _context should be outside onConfigChange to avoid creating new instance every time
                _context = new TelemetryContext(config, _config, core, _self._unloadHooks);
                if (core && core.setTraceCtx) {
                    core.setTraceCtx(_context.getTraceCtx());
                }
            }
            function _addPropertiesIfAbsent(inputMap, outputMap) {
                if (inputMap) {
                    objForEachKey(inputMap, function (name, inputValue) {
                        if (!outputMap[name]) {
                            outputMap[name] = inputValue;
                        }
                    });
                }
            }
        });
        return _this;
    }
    PropertiesPlugin.prototype.initialize = function (coreConfig, core, extensions) {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
    };
    /**
     * Process the event and add part A fields to it.
     * @param event - The event that needs to be stored.
     * @param itemCtx - This is the context for the current request, ITelemetryPlugin instances
     * can optionally use this to access the current core instance or define / pass additional information
     * to later plugins (vs appending items to the telemetry item)
     */
    PropertiesPlugin.prototype.processTelemetry = function (event, itemCtx) {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
    };
    /**
     * Get properties context to override or specify specific part A properties
     * @returns the Context
     */
    PropertiesPlugin.prototype.getPropertiesContext = function () {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
        return null;
    };
    /**
     * Sets a custom property to be sent with every event. IEventProperty can be used to tag the property as
     * pii or customer content.
     * @param name - The name of the property.
     * @param value - The context property's value.
     */
    PropertiesPlugin.prototype.setProperty = function (name, value) {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
    };
    return PropertiesPlugin;
}(BaseTelemetryPlugin));
export { PropertiesPlugin };
//# sourceMappingURL=PropertiesPlugin.js.map